import * as React from 'react'

import { Box, Button, InfoDot, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { Option } from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { durationToText } from 'modules/core/lib/duration'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import DeleteWeek from 'modules/shift_management/components/Settings/ShiftTemplates/Content/EditShiftTemplate/WeekSection/DeleteWeek'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { getColor } from 'modules/shift_management/lib/getColor'
import { totalDurationFromUnpaidBreaks } from 'modules/shift_management/lib/totalDurationFromUnpaidBreaks'
import shiftConfigurations from 'modules/shift_management/store/api/collections/shiftConfigurations'
import type ShiftConfiguration from 'modules/shift_management/store/api/models/ShiftConfiguration'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'

interface Props {
  week: TemplateWeek
  shiftTemplateId: string
}

const totalHoursCopy = (
  shiftConfigs: ShiftConfiguration[],
  plannedBreaks: PlannedBreaks,
  isTimeFormat24H: boolean
): string => {
  const shiftDuration = shiftConfigs.reduce((acc, shiftConfig) => {
    const breaks = plannedBreaks.filterByShiftConfiguration(shiftConfig.id)
    return acc + shiftConfig.duration(isTimeFormat24H) - totalDurationFromUnpaidBreaks(breaks)
  }, 0)

  return i18n.t('shift_management.templates.weeks.hours', {
    hours: durationToText(shiftDuration / 60, 'minutes'),
  })
}

const totalRestDaysCopy = (shiftConfigs: ShiftConfiguration[]): string => {
  const restDays = shiftConfigs.filter((shiftConfig) => shiftConfig.isRestDay)

  return i18n.tp('shift_management.templates.weeks.rest_days_total', {
    count: restDays.length,
  })
}

const WeekItem = ({ week, shiftTemplateId }: Props) => {
  const [open, toggle] = useToggle(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const weekId = week.attributes.id
  const name = week.attributes.name
  const color = getColor(week.attributes.color)
  const { navigation } = useNavigation()
  const restDayFlag = hasFeature('shift_management_rest_day')
  const showColor = hasFeature('shift_management_colours_for_shifts')
  const shiftConfigs = shiftConfigurations.filter({
    template_week_id: week.attributes.id,
  })
  const shiftConfigurationIds = shiftConfigs.map((config) => config.id)

  const { isTimeFormat24H } = useIsTimeFormat24H()

  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { shiftConfigurationIds },
    skip: !shiftConfigurationIds.length,
  })

  const options: Option[] = [
    {
      title: i18n.t('shift_management.rotating_shifts.schedules.edit'),
      to: navigation.shifts.templates.templatesShiftTemplateId.edit.weeksWeekIdEdit.fullPath({
        shiftTemplateId: shiftTemplateId.toString(),
        weekId: weekId.toString(),
      }),
    },
    {
      title: i18n.t('shift_management.rotating_shifts.schedules.delete'),
      onClick: () => setIsModalOpen(true),
      type: 'wrong',
    },
  ]

  const description = restDayFlag
    ? `${totalHoursCopy(
        shiftConfigs,
        plannedBreaksCollection,
        isTimeFormat24H
      )}, ${totalRestDaysCopy(shiftConfigs)}`
    : totalHoursCopy(shiftConfigs, plannedBreaksCollection, isTimeFormat24H)

  return (
    <Box
      alignItems="center"
      flexDirection="row"
      flexGrow
      justifyContent="spaceBetween"
      paddingX={showColor ? 's24' : 's32'}
      paddingY="s24"
    >
      <Box width="full" flexDirection="column" justifyContent="spaceBetween" alignItems="flexStart">
        <Box flexDirection="row" alignItems="center" gap="s8">
          {showColor && <InfoDot size="small" color={color} />}
          <Text.Normal weight="semibold">{name}</Text.Normal>
        </Box>
        <Box paddingX={showColor ? 's16' : undefined}>
          <Text.Normal size="200" color="grey600">
            {description}
          </Text.Normal>
        </Box>
      </Box>
      <Popover attachment="top center" toggle={toggle} open={open}>
        <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
        <PopoverContent onClose={toggle(false)} options={options} />
      </Popover>
      {isModalOpen && (
        <DeleteWeek
          weekId={weekId}
          shiftTemplateId={shiftTemplateId}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </Box>
  )
}

export default observer(WeekItem)
